import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { ShieldCheck, ArrowLeft, Check, X, Flag } from "lucide-react";

const ALLOWED = [
  "Objets d'occasion entre particuliers (meubles, électroménager, vélos, livres, jeux…)",
  "Dons et objets gratuits à récupérer",
  "Annonces honnêtes, avec photos réelles de l'objet proposé",
  "Annonces professionnelles clairement identifiées comme telles",
];
const FORBIDDEN = [
  "Armes, munitions, produits dangereux ou réglementés",
  "Médicaments, tabac, alcool, produits illicites",
  "Animaux vivants",
  "Contrefaçons et produits volés",
  "Contenus trompeurs, arnaques, fausses annonces ou faux prix",
  "Coordonnées personnelles d'un tiers sans son accord",
  "Contenus à caractère haineux, violent ou pornographique",
];

export default function AnnonceRules() {
  useSeo({ title: "Règles de publication des petites annonces | PROXIreduc-28", canonicalPath: "/petites-annonces/regles" });
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 pb-24" data-testid="annonce-rules-page">
      <Link to="/petites-annonces" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#E05638]"><ArrowLeft size={14} /> Retour aux annonces</Link>
      <div className="mt-4 inline-flex items-center gap-2 text-[#E05638] font-semibold text-sm"><ShieldCheck size={16} /> Charte & modération</div>
      <h1 className="mt-1 font-heading text-3xl font-extrabold text-slate-900">Règles de publication</h1>
      <p className="mt-2 text-slate-600">Les petites annonces PROXIreduc-28 sont un service local et gratuit entre habitants d'Eure-et-Loir. Pour garder un espace utile et sûr, merci de respecter ces règles.</p>

      <section className="mt-8">
        <h2 className="font-heading text-xl font-bold text-slate-900 flex items-center gap-2"><Check className="text-emerald-600" size={20} /> Ce qui est autorisé</h2>
        <ul className="mt-3 space-y-2">
          {ALLOWED.map((t) => <li key={t} className="flex items-start gap-2 text-sm text-slate-700"><Check size={16} className="text-emerald-600 shrink-0 mt-0.5" /> {t}</li>)}
        </ul>
      </section>

      <section className="mt-8">
        <h2 className="font-heading text-xl font-bold text-slate-900 flex items-center gap-2"><X className="text-rose-600" size={20} /> Ce qui est interdit</h2>
        <ul className="mt-3 space-y-2">
          {FORBIDDEN.map((t) => <li key={t} className="flex items-start gap-2 text-sm text-slate-700"><X size={16} className="text-rose-600 shrink-0 mt-0.5" /> {t}</li>)}
        </ul>
      </section>

      <section className="mt-8 bg-slate-50 rounded-2xl p-5">
        <h2 className="font-heading text-lg font-bold text-slate-900">Modération et signalement</h2>
        <p className="mt-2 text-sm text-slate-700">Votre annonce est publiée immédiatement puis vérifiée par notre équipe. Une annonce non conforme peut être suspendue ou supprimée, et vous en êtes informé par e-mail avec le motif. Chaque annonce comporte un bouton <strong>« Signaler »</strong> <Flag size={13} className="inline" /> ; tout signalement est enregistré et examiné par un administrateur.</p>
        <p className="mt-2 text-sm text-slate-700">Les annonces professionnelles doivent être clairement identifiées. Les comptes à l'origine d'abus répétés peuvent être temporairement bloqués.</p>
      </section>

      <section className="mt-8">
        <h2 className="font-heading text-lg font-bold text-slate-900">Conseils de sécurité</h2>
        <ul className="mt-3 space-y-2 text-sm text-slate-700">
          <li className="flex items-start gap-2"><Check size={16} className="text-slate-400 shrink-0 mt-0.5" /> Privilégiez la remise en main propre dans un lieu public.</li>
          <li className="flex items-start gap-2"><Check size={16} className="text-slate-400 shrink-0 mt-0.5" /> Ne versez jamais d'acompte avant d'avoir vu l'objet.</li>
          <li className="flex items-start gap-2"><Check size={16} className="text-slate-400 shrink-0 mt-0.5" /> Ne communiquez jamais vos coordonnées bancaires.</li>
        </ul>
      </section>

      <p className="mt-8 text-xs text-slate-400">Consultez aussi nos <Link to="/cgu" className="underline hover:text-[#E05638]">CGU</Link>, <Link to="/mentions-legales" className="underline hover:text-[#E05638]">mentions légales</Link> et notre <Link to="/confidentialite" className="underline hover:text-[#E05638]">politique de confidentialité</Link>.</p>

      <div className="mt-8 flex gap-2">
        <Link to="/petites-annonces/deposer" className="inline-flex items-center gap-1 btn-shine text-white font-semibold px-5 py-2.5 rounded-xl">J'ai compris, déposer une annonce</Link>
      </div>
    </div>
  );
}
